import { ExportStatus, ExportType } from "api/reactQuery/queries/export.types"
import dayjs from "dayjs"

export const EXPORT_TASKS = "exportTasks"

export interface ExportTask {
  id: number
  filename: string
  created_at: string
  status: ExportStatus
  download_url?: string | null
  classId?: number
}

export type ExportTasksStorage = Record<string, ExportTask[]>

/**
 * Get all export tasks from sessionStorage
 */
export const getAllExportTasks = (): ExportTasksStorage => {
  const jsonSessionStorage = sessionStorage.getItem(EXPORT_TASKS)
  if (!jsonSessionStorage) return {}

  try {
    return JSON.parse(jsonSessionStorage)
  } catch {
    return {}
  }
}

/**
 * Get export tasks for a specific export type
 */
export const getExportTasksByType = (exportType: ExportType) => {
  const allTasks = getAllExportTasks()
  return allTasks[exportType] || []
}

/**
 * Save export tasks to sessionStorage
 */
export const saveExportTasks = (tasks: ExportTasksStorage) => {
  sessionStorage.setItem(EXPORT_TASKS, JSON.stringify(tasks))
}

/**
 * Add or update a single export task
 */
export const addOrUpdateExportTask = (
  exportType: ExportType,
  taskData: ExportTask,
) => {
  const allTasks = getAllExportTasks()

  if (!allTasks[exportType]) {
    allTasks[exportType] = []
  }

  const index = allTasks[exportType].findIndex(
    (element) => element.id === taskData.id,
  )

  if (index === -1) {
    allTasks[exportType].push(taskData)
  } else {
    allTasks[exportType][index] = taskData
  }

  saveExportTasks(allTasks)

  // Emit custom event to notify GlobalExportReportsPolling about new task
  window.dispatchEvent(new CustomEvent("exportTaskAdded"))
}

/**
 * Drop a report from the local list. The file stays on the server until it expires.
 * One argument removes that id from every export type (menus that don't know the type).
 * Two arguments remove it only from the given type, so the same id in another export stays.
 */
export const removeExportTask = (
  exportTypeOrTaskId: ExportType | number,
  taskId?: number,
) => {
  const allTasks = getAllExportTasks()

  if (typeof exportTypeOrTaskId === "number") {
    Object.keys(allTasks).forEach((exportType) => {
      allTasks[exportType] = allTasks[exportType].filter(
        (element) => element.id !== exportTypeOrTaskId,
      )
    })
  } else if (taskId !== undefined && allTasks[exportTypeOrTaskId]) {
    allTasks[exportTypeOrTaskId] = allTasks[exportTypeOrTaskId].filter(
      (element) => element.id !== taskId,
    )
  } else {
    return
  }

  saveExportTasks(allTasks)
  window.dispatchEvent(new CustomEvent("exportTaskAdded"))
}

/**
 * Clean up export tasks older than 24 hours
 */
export const cleanupOldExportTasks = (): void => {
  const allTasks = getAllExportTasks()
  const now = dayjs()
  const cleanedTasks: ExportTasksStorage = {}

  Object.keys(allTasks).forEach((exportType) => {
    const validEntries = allTasks[exportType].filter((entry) => {
      const createdAt = dayjs(entry.created_at)
      const hoursDiff = now.diff(createdAt, "hour")
      return hoursDiff < 24
    })

    if (validEntries.length > 0) {
      cleanedTasks[exportType] = validEntries
    }
  })

  if (Object.keys(cleanedTasks).length > 0) {
    saveExportTasks(cleanedTasks)
  } else {
    sessionStorage.removeItem(EXPORT_TASKS)
  }
}

/**
 * Filter tasks by status
 */
export const filterTasksByStatus = (
  tasks: ExportTask[],
  status: ExportStatus,
): ExportTask[] => {
  return tasks.filter((task) => task.status === status)
}

/**
 * Filter tasks by classId
 */
export const filterTasksByClassId = (
  tasks: ExportTask[],
  classId?: number,
): ExportTask[] => {
  if (!classId) return tasks
  return tasks.filter((task) => task.classId === classId)
}
